/**
 * Orders Toolbar V2 - Comprehensive Filtering with BaseDrawer
 *
 * Uses draft state pattern: filters are collected in local state,
 * then applied when user clicks "Apply Filters"
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useTranslation, useIsRTL } from '@spcc/core'
import {
  Button,
  Input,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Badge,
  Checkbox,
  Label,
  BaseDrawer,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DateRangePicker,
  cn,
  Search,
  Columns,
  X,
  Filter,
  ChevronDown,
  ChevronUp,
  Share,
  Sliders,
  CircleDot,
  Calendar,
  Sparkles,
  Loader2,
  Package,
  Download,
  Trash2,
  Check,
  Square,
  Minus,
  Truck,
  ShieldAlert,
} from '@spcc/uikit'
import type { OrderStatus } from '../../../../domain/types'
import { ORDER_STATUS_OPTIONS } from '../../../../domain/filter-config'
import type { OrderTableColumnKey } from '../types'

// ============================================
// Types
// ============================================

// Work around react-i18next children typing mismatch across React type roots.
type I18nChild = any

interface DateRangeValue {
  from: string
  to: string
}

interface ColumnOption {
  key: OrderTableColumnKey
  labelKey: string
  locked?: boolean
}

interface OrdersToolbarV2Props {
  // Search
  searchValue: string
  onSearchChange: (value: string) => void
  onSearchSubmit?: (value: string) => void

  // Multi-select filters
  statusFilter: OrderStatus[]
  onStatusChange: (statuses: OrderStatus[]) => void

  // Product filter
  productFilter?: string[]
  onProductChange?: (products: string[]) => void
  productOptions?: Array<{ value: string; label: string }>

  // Delivery address filter
  deliveryAddressFilter?: string[]
  onDeliveryAddressChange?: (addresses: string[]) => void
  deliveryAddressOptions?: Array<{ value: string; label: string }>

  // License plate filter
  vehicleLicensePlateFilter?: string
  onVehicleLicensePlateChange?: (lp: string | undefined) => void

  // Date range (order date)
  dateFrom?: string
  dateTo?: string
  onDateFromChange: (date: string | undefined) => void
  onDateToChange: (date: string | undefined) => void

  // Date range (delivery date)
  deliveryDateFrom?: string
  deliveryDateTo?: string
  onDeliveryDateFromChange: (date: string | undefined) => void
  onDeliveryDateToChange: (date: string | undefined) => void

  // Columns
  columnOptions: ColumnOption[]
  visibleColumns: OrderTableColumnKey[]
  onVisibleColumnsChange: (columns: OrderTableColumnKey[]) => void
  onResetColumns?: () => void

  // Loading state
  isLoading?: boolean

  // Filter count for badge
  activeFilterCount?: number
  onClearFilters?: () => void

  // Share URL
  onShareUrl?: () => void

  // Batch apply all filters at once (avoids race conditions)
  onApplyAllFilters?: (filters: {
    status: OrderStatus[]
    products: string[]
    deliveryAddresses: string[]
    dateFrom: string | undefined
    dateTo: string | undefined
    deliveryDateFrom: string | undefined
    deliveryDateTo: string | undefined
    vehicleLicensePlate: string | undefined
  }) => void

  // Bulk selection props
  selectedCount?: number
  totalOrdersCount?: number
  isAllOnPageSelected?: boolean
  isSomeOnPageSelected?: boolean
  onSelectAllOnPage?: () => void
  onDeselectAllOnPage?: () => void
  onSelectAllAcrossPages?: () => void
  onClearSelection?: () => void
  onDownloadSelectedPDFs?: () => void
  isBulkDownloading?: boolean
  canDownloadBulkPDF?: boolean
  onBulkCancelSelected?: () => void
  canBulkCancelSelected?: boolean
  isBulkCancelling?: boolean

  // Super-admin: any-status select + force update
  isSuperAdmin?: boolean
  isAllOnPageSelectedAnyStatus?: boolean
  isSomeOnPageSelectedAnyStatus?: boolean
  onSelectAllAnyStatus?: () => void
  onDeselectAllAnyStatus?: () => void
  onForceStatusUpdate?: () => void
  canForceStatusUpdate?: boolean
  isBulkForceUpdating?: boolean
}

// Draft filter state (local to filter panel)
interface FilterDraft {
  status: OrderStatus[]
  products: string[]
  deliveryAddresses: string[]
  dateFrom: string | undefined
  dateTo: string | undefined
  deliveryDateFrom: string | undefined
  deliveryDateTo: string | undefined
  vehicleLicensePlate: string | undefined
}

// ============================================
// Helper Functions
// ============================================

function buildStatusOptions(t: (key: string) => string): Array<{ value: string; label: string; color?: string }> {
  return ORDER_STATUS_OPTIONS.map((opt) => ({
    value: opt.value,
    label: t(opt.labelKey),
    color: opt.color,
  }))
}

function getStatusColor(color?: string): string {
  const colors: Record<string, string> = {
    green: 'bg-green-500',
    amber: 'bg-amber-500',
    blue: 'bg-blue-500',
    gray: 'bg-gray-400',
    red: 'bg-red-500',
  }
  return colors[color ?? ''] ?? 'bg-gray-400'
}

function isColumnVisible(visibleColumns: OrderTableColumnKey[], key: OrderTableColumnKey): boolean {
  return visibleColumns.includes(key)
}

function getSortedVisibleColumns(
  nextVisibleSet: Set<OrderTableColumnKey>,
  columnOptions: ColumnOption[]
): OrderTableColumnKey[] {
  return columnOptions
    .map((column) => column.key)
    .filter((key) => nextVisibleSet.has(key))
}

function toggleColumnVisibility(
  visibleColumns: OrderTableColumnKey[],
  columnOptions: ColumnOption[],
  key: OrderTableColumnKey,
  checked: boolean
): OrderTableColumnKey[] {
  const targetColumn = columnOptions.find((column) => column.key === key)
  if (!targetColumn || targetColumn.locked) return visibleColumns

  const nextVisibleSet = new Set(visibleColumns)
  if (checked) {
    nextVisibleSet.add(key)
  } else {
    nextVisibleSet.delete(key)
  }

  for (const column of columnOptions) {
    if (column.locked) {
      nextVisibleSet.add(column.key)
    }
  }

  return getSortedVisibleColumns(nextVisibleSet, columnOptions)
}

// ============================================
// Sub-Components
// ============================================

interface CollapsibleSectionProps {
  title: string
  activeCount: number
  defaultOpen?: boolean
  children: I18nChild
  isRTL?: boolean
  icon?: I18nChild
}

function CollapsibleSection({
  title,
  activeCount,
  defaultOpen = false,
  children,
  isRTL = false,
  icon,
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="rounded-lg border bg-card">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex w-full items-center justify-between p-3 text-sm font-medium',
            'hover:bg-muted/50 transition-colors rounded-lg',
          )}
        >
          <span className={cn('flex items-center gap-2.5')}>
            {icon && <span className="text-muted-foreground">{icon}</span>}
            <span className="font-medium">{title}</span>
            {activeCount > 0 && (
              <Badge className="h-5 min-w-5 px-1.5 text-xs font-semibold bg-primary text-primary-foreground">
                {activeCount}
              </Badge>
            )}
          </span>
          <div className={cn(
            'h-6 w-6 rounded-full flex items-center justify-center transition-colors',
            isOpen ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
          )}>
            {isOpen ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
          </div>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-3 pb-3 pt-1">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  )
}

interface CheckboxListProps {
  options: Array<{ value: string; label: string; color?: string; description?: string }>
  selected: string[]
  onChange: (values: string[]) => void
  searchable?: boolean
  searchPlaceholder?: string
  maxHeight?: number
  isRTL?: boolean
}

function CheckboxList({
  options,
  selected,
  onChange,
  searchable = false,
  searchPlaceholder,
  maxHeight = 200,
  isRTL = false,
}: CheckboxListProps) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const placeholder = searchPlaceholder || t('common.search')

  const filteredOptions = useMemo(() => {
    if (!search) return options
    const searchLower = search.toLowerCase()
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(searchLower) ||
        opt.description?.toLowerCase().includes(searchLower)
    )
  }, [options, search])

  const handleToggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value))
    } else {
      onChange([...selected, value])
    }
  }

  return (
    <div className="space-y-3">
      {searchable && (
        <div className="relative">
          <Search className={cn(
            'absolute top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground',
            isRTL ? 'right-3' : 'left-3'
          )} />
          <Input
            type="text"
            placeholder={placeholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={cn(
              'h-9 text-sm bg-muted/30 border-muted-foreground/20 focus:bg-background',
              isRTL ? 'pr-9' : 'pl-9'
            )}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className={cn(
                'absolute top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted',
                isRTL ? 'left-2' : 'right-2'
              )}
            >
              <X className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          )}
        </div>
      )}
      <div
        className="space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent"
        style={{ maxHeight: `${maxHeight}px` }}
      >
        {filteredOptions.length === 0 ? (
          <div className="py-6 text-center">
            <div className="text-muted-foreground/50 mb-1">
              <Search className="h-8 w-8 mx-auto" />
            </div>
            <p className="text-sm text-muted-foreground">{t('common.noOptionsFound')}</p>
          </div>
        ) : (
          filteredOptions.map((opt) => {
            const isSelected = selected.includes(opt.value)
            return (
              <label
                key={opt.value}
                className={cn(
                  'flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-[background-color,border-color]',
                  'border border-transparent',
                  isSelected
                    ? 'bg-primary/5 border-primary/20 shadow-sm'
                    : 'hover:bg-muted/50',
                )}
              >
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => handleToggle(opt.value)}
                  className={cn(
                    'shrink-0 transition-[border-color,background-color]',
                    isSelected && 'border-primary'
                  )}
                />
                <div className={cn('flex items-center gap-2.5 flex-1 min-w-0')}>
                  {opt.color && (
                    <span className={cn(
                      'w-3 h-3 rounded-full shrink-0 ring-2 ring-background shadow-sm',
                      getStatusColor(opt.color)
                    )} />
                  )}
                  <span className={cn(
                    'text-sm truncate',
                    isSelected ? 'font-medium text-foreground' : 'text-muted-foreground'
                  )}>
                    {opt.label}
                  </span>
                </div>
                {isSelected && (
                  <span className="shrink-0 text-primary">
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                )}
              </label>
            )
          })
        )}
      </div>
    </div>
  )
}

// ============================================
// Main Component
// ============================================

export function OrdersToolbarV2({
  searchValue,
  onSearchChange,
  onSearchSubmit,
  statusFilter,
  onStatusChange,
  productFilter = [],
  onProductChange,
  productOptions = [],
  deliveryAddressFilter = [],
  onDeliveryAddressChange,
  deliveryAddressOptions = [],
  vehicleLicensePlateFilter,
  onVehicleLicensePlateChange,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  deliveryDateFrom,
  deliveryDateTo,
  onDeliveryDateFromChange,
  onDeliveryDateToChange,
  columnOptions,
  visibleColumns,
  onVisibleColumnsChange,
  onResetColumns,
  isLoading = false,
  activeFilterCount = 0,
  onClearFilters,
  onShareUrl,
  onApplyAllFilters,
  // Bulk selection props
  selectedCount = 0,
  totalOrdersCount = 0,
  isAllOnPageSelected = false,
  isSomeOnPageSelected = false,
  onSelectAllOnPage,
  onDeselectAllOnPage,
  onSelectAllAcrossPages,
  onClearSelection,
  onDownloadSelectedPDFs,
  isBulkDownloading = false,
  canDownloadBulkPDF = true,
  onBulkCancelSelected,
  canBulkCancelSelected = false,
  isBulkCancelling = false,
  // Super-admin props
  isSuperAdmin = false,
  isAllOnPageSelectedAnyStatus = false,
  isSomeOnPageSelectedAnyStatus = false,
  onSelectAllAnyStatus,
  onDeselectAllAnyStatus,
  onForceStatusUpdate,
  canForceStatusUpdate = false,
  isBulkForceUpdating = false,
}: OrdersToolbarV2Props) {
  const { t } = useTranslation()
  const isRTL = useIsRTL()
  const [isOpen, setIsOpen] = useState(false)

  // Local search state - only submits on Enter or button click
  const [localSearch, setLocalSearch] = useState(searchValue)

  // Sync local search with external value when it changes externally
  useEffect(() => {
    setLocalSearch(searchValue)
  }, [searchValue])

  // Handle search submission (Enter key or button click)
  const handleSearchSubmit = useCallback(() => {
    // Update local state
    if (localSearch !== searchValue) {
      onSearchChange(localSearch)
    }
    // Trigger URL update with current value
    onSearchSubmit?.(localSearch)
  }, [localSearch, searchValue, onSearchChange, onSearchSubmit])

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSearchSubmit()
    }
  }, [handleSearchSubmit])

  const handleClearSearch = useCallback(() => {
    setLocalSearch('')
    onSearchChange('')
    onSearchSubmit?.('')
  }, [onSearchChange, onSearchSubmit])

  // Draft state for filter panel (only applied when user clicks Apply)
  const [draft, setDraft] = useState<FilterDraft>({
    status: statusFilter,
    products: productFilter,
    deliveryAddresses: deliveryAddressFilter,
    dateFrom,
    dateTo,
    deliveryDateFrom,
    deliveryDateTo,
    vehicleLicensePlate: vehicleLicensePlateFilter,
  })

  const effectiveIsAllOnPageSelected = isSuperAdmin ? isAllOnPageSelectedAnyStatus : isAllOnPageSelected
  const effectiveIsSomeOnPageSelected = isSuperAdmin ? isSomeOnPageSelectedAnyStatus : isSomeOnPageSelected

  // Track previous isOpen state to detect when panel opens
  const prevIsOpenRef = useRef(isOpen)

  // Sync draft with external filter ONLY when panel opens (not on every prop change)
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current
    prevIsOpenRef.current = isOpen

    // Only sync when panel transitions from closed to open
    if (isOpen && !wasOpen) {
      setDraft({
        status: statusFilter,
        products: productFilter,
        deliveryAddresses: deliveryAddressFilter,
        dateFrom,
        dateTo,
        deliveryDateFrom,
        deliveryDateTo,
        vehicleLicensePlate: vehicleLicensePlateFilter,
      })
    }
  }, [isOpen, statusFilter, productFilter, deliveryAddressFilter, dateFrom, dateTo, deliveryDateFrom, deliveryDateTo, vehicleLicensePlateFilter])

  // Build options
  const statusOptions = useMemo(() => buildStatusOptions(t), [t])
  const visibleColumnCount = useMemo(() => visibleColumns.length, [visibleColumns])

  // Count draft active filters
  const draftActiveCount = useMemo(() => {
    let count = 0
    if (draft.status.length > 0) count += draft.status.length
    if (draft.products.length > 0) count += draft.products.length
    if (draft.deliveryAddresses.length > 0) count += draft.deliveryAddresses.length
    if (draft.dateFrom || draft.dateTo) count += 1
    if (draft.deliveryDateFrom || draft.deliveryDateTo) count += 1
    if (draft.vehicleLicensePlate) count += 1
    return count
  }, [draft])

  // Handlers
  const handleApplyFilters = useCallback(() => {
    // Use batch handler if available (preferred - avoids race conditions)
    if (onApplyAllFilters) {
      onApplyAllFilters({
        status: draft.status,
        products: draft.products,
        deliveryAddresses: draft.deliveryAddresses,
        dateFrom: draft.dateFrom,
        dateTo: draft.dateTo,
        deliveryDateFrom: draft.deliveryDateFrom,
        deliveryDateTo: draft.deliveryDateTo,
        vehicleLicensePlate: draft.vehicleLicensePlate,
      })
    } else {
      // Fallback: apply individually (may have race conditions)
      onStatusChange(draft.status)
      onProductChange?.(draft.products)
      onDeliveryAddressChange?.(draft.deliveryAddresses)
      onDateFromChange(draft.dateFrom)
      onDateToChange(draft.dateTo)
      onDeliveryDateFromChange?.(draft.deliveryDateFrom)
      onDeliveryDateToChange?.(draft.deliveryDateTo)
      onVehicleLicensePlateChange?.(draft.vehicleLicensePlate)
    }
    setIsOpen(false)
  }, [draft, onStatusChange, onProductChange, onDeliveryAddressChange, onDateFromChange, onDateToChange, onDeliveryDateFromChange, onDeliveryDateToChange, onVehicleLicensePlateChange, onApplyAllFilters])

  const handleClearDraft = useCallback(() => {
    setDraft({
      status: [],
      products: [],
      deliveryAddresses: [],
      dateFrom: undefined,
      dateTo: undefined,
      deliveryDateFrom: undefined,
      deliveryDateTo: undefined,
      vehicleLicensePlate: undefined,
    })
  }, [])

  const handleClearAllAndApply = useCallback(() => {
    handleClearDraft()
    onClearFilters?.()
    setIsOpen(false)
  }, [handleClearDraft, onClearFilters])

  const handleColumnVisibilityChange = useCallback((key: OrderTableColumnKey, checked: boolean) => {
    const nextVisibleColumns = toggleColumnVisibility(visibleColumns, columnOptions, key, checked)
    onVisibleColumnsChange(nextVisibleColumns)
  }, [visibleColumns, columnOptions, onVisibleColumnsChange])

  const handleResetColumns = useCallback(() => {
    onResetColumns?.()
  }, [onResetColumns])

  // Build header content for BaseDrawer
  const filterHeaderContent = (
    <div className={cn('flex items-center gap-3')}>
      <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center">
        <Sliders className="h-5 w-5 text-white" />
      </div>
      <div className={cn('flex flex-col', isRTL && 'items-end')}>
        <span className="text-lg font-semibold text-white">{t('common.filters')}</span>
        {draftActiveCount > 0 && (
          <span className="text-xs text-white/70">
            {draftActiveCount} {t('common.filtersActive')}
          </span>
        )}
      </div>
    </div>
  )

  // Build footer content for BaseDrawer
  const filterFooterContent = (
    <div className={cn('flex items-center gap-3')}>
      {onShareUrl && (
        <Button variant="outline" size="icon" onClick={onShareUrl} title={t('common.shareUrl')} className="shrink-0">
          <Share className="h-4 w-4" />
        </Button>
      )}
      <div className="flex-1" />
      <Button variant="ghost" onClick={handleClearAllAndApply} className="text-muted-foreground">
        <X className="h-4 w-4 me-2" />
        {t('common.clearAll')}
      </Button>
      <Button onClick={handleApplyFilters} className="gap-2 min-w-[120px]">
        <Sparkles className="h-4 w-4" />
        {t('common.apply')}
        {draftActiveCount > 0 && (
          <Badge variant="secondary" className="h-5 px-1.5 text-xs bg-primary-foreground/20">
            {draftActiveCount}
          </Badge>
        )}
      </Button>
    </div>
  )

  return (
    <div className={cn(
      'flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'
    )}>
      {/* Left side: Bulk Actions + Search + Filters */}
      <div className={cn(
        'flex flex-1 items-center gap-2'
      )}>
        {/* Bulk Selection Actions - shown when items are selected */}
        {selectedCount > 0 && (
          <div className="flex items-center gap-2">
            {/* Selection dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                    <div className="relative h-4 w-4 border rounded flex items-center justify-center">
                      {effectiveIsSomeOnPageSelected ? (
                        <Minus className="h-3 w-3" />
                      ) : effectiveIsAllOnPageSelected ? (
                        <Check className="h-3 w-3" />
                      ) : null}
                    </div>
                  <span>{selectedCount} {t('orders.bulkActions.selected')}</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align={isRTL ? 'end' : 'start'}>
                {/* Super-admin: select/deselect all any-status */}
                {isSuperAdmin && !isAllOnPageSelectedAnyStatus && (
                  <DropdownMenuItem onClick={onSelectAllAnyStatus}>
                    <Check className="h-4 w-4 me-2" />
                    {t('orders.bulkActions.selectAllAnyStatus')}
                  </DropdownMenuItem>
                )}
                {isSuperAdmin && isAllOnPageSelectedAnyStatus && (
                  <DropdownMenuItem onClick={onDeselectAllAnyStatus}>
                    <Square className="h-4 w-4 me-2" />
                    {t('orders.bulkActions.deselectAllAnyStatus')}
                  </DropdownMenuItem>
                )}
                {/* Standard pending-only selection */}
                {!isSuperAdmin && !isAllOnPageSelected && (
                  <DropdownMenuItem onClick={onSelectAllOnPage}>
                    <Check className="h-4 w-4 me-2" />
                    {t('orders.bulkActions.selectAllOnPage')}
                  </DropdownMenuItem>
                )}
                {!isSuperAdmin && isAllOnPageSelected && (
                  <DropdownMenuItem onClick={onDeselectAllOnPage}>
                    <Square className="h-4 w-4 me-2" />
                    {t('orders.bulkActions.deselectAllOnPage')}
                  </DropdownMenuItem>
                )}
                {totalOrdersCount > selectedCount && (
                  <DropdownMenuItem onClick={onSelectAllAcrossPages}>
                    <Check className="h-4 w-4 me-2" />
                    {t('orders.bulkActions.selectAllOrders', { count: totalOrdersCount })}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onClearSelection}>
                  <X className="h-4 w-4 me-2" />
                  {t('orders.bulkActions.clearSelection')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Download PDF button */}
            <Button
              variant="default"
              size="sm"
              className="gap-2"
              onClick={onDownloadSelectedPDFs}
              disabled={isBulkDownloading || !canDownloadBulkPDF}
              title={!canDownloadBulkPDF ? t('orders.bulkActions.maxOrdersExceeded') : undefined}
            >
              {isBulkDownloading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">{t('orders.bulkActions.downloadPDF')}</span>
            </Button>

            {canBulkCancelSelected && (
              <Button
                variant="destructive"
                size="sm"
                className="gap-2"
                onClick={onBulkCancelSelected}
                disabled={isBulkCancelling}
              >
                {isBulkCancelling ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">{t('orders.bulkActions.cancelSelected')}</span>
              </Button>
            )}

            {/* Force Status Update button — super_admin only */}
            {isSuperAdmin && (
              <Button
                variant="destructive"
                size="sm"
                className="gap-2 border-2 border-destructive/70 bg-destructive/90 hover:bg-destructive"
                onClick={onForceStatusUpdate}
                disabled={!canForceStatusUpdate || isBulkForceUpdating}
                title={t('orders.bulkActions.forceStatusUpdate')}
              >
                {isBulkForceUpdating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ShieldAlert className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">
                  {isBulkForceUpdating
                    ? t('orders.bulkActions.forceStatusUpdating')
                    : t('orders.bulkActions.forceStatusUpdate')}
                </span>
              </Button>
            )}

            {/* Divider */}
            <div className="h-6 w-px bg-border mx-1" />
          </div>
        )}

        {/* Search with submit button */}
        <div className="flex items-center gap-0 flex-1 max-w-md">
          <div className="relative flex-1">
            <Search className={cn(
              'absolute top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none',
              isRTL ? 'right-3' : 'left-3'
            )} />
            <Input
              type="text"
              placeholder={t('orders.searchPlaceholder')}
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className={cn(
                'h-10 bg-muted/30 border-muted-foreground/20 focus:bg-background transition-colors',
                isRTL ? 'pr-10 pl-8 rounded-l-none rounded-r-md' : 'pl-10 pr-8 rounded-r-none rounded-l-md'
              )}
            />
            {localSearch && (
              <button
                type="button"
                onClick={handleClearSearch}
                className={cn(
                  'absolute top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-muted/80 transition-colors',
                  isRTL ? 'left-2' : 'right-2'
                )}
              >
                <X className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            )}
          </div>
          <Button
            onClick={handleSearchSubmit}
            size="default"
            disabled={isLoading}
            className={cn(
              'h-10 px-4 shrink-0',
              isRTL ? 'rounded-r-none rounded-l-md' : 'rounded-l-none rounded-r-md'
            )}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            <span className="hidden sm:inline ms-2">{t('common.search')}</span>
          </Button>
        </div>

        {/* Filter Panel Trigger Button */}
        <Button variant="outline" className="gap-2" disabled={isLoading} onClick={() => setIsOpen(true)}>
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Filter className="h-4 w-4" />
          )}
          <span className="hidden sm:inline">{t('common.filters')}</span>
          {activeFilterCount > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs">
              {activeFilterCount}
            </Badge>
          )}
        </Button>

        {/* Filter Panel Drawer */}
        <BaseDrawer
          open={isOpen}
          onOpenChange={setIsOpen}
          side="right"
          isRTL={isRTL}
          title={t('common.filters')}
          headerGradient="from-primary to-primary/80"
          headerContent={filterHeaderContent}
          footerContent={filterFooterContent}
          fixedWidth={420}
          tabletWidth={380}
          mobileWidth="100%"
        >
          <div className="space-y-3 -mx-6 -my-5 px-6 py-4">
            {/* Status Filter */}
            <CollapsibleSection
              title={t('orders.filterByStatus')}
              activeCount={draft.status.length}
              defaultOpen
              isRTL={isRTL}
              icon={<CircleDot className="h-4 w-4" />}
            >
              <CheckboxList
                options={statusOptions}
                selected={draft.status}
                onChange={(values) => setDraft(prev => ({ ...prev, status: values as OrderStatus[] }))}
                isRTL={isRTL}
              />
            </CollapsibleSection>

            {/* Product Filter */}
            <CollapsibleSection
              title={t('orders.filterByProduct')}
              activeCount={draft.products.length}
              isRTL={isRTL}
              icon={<Package className="h-4 w-4" />}
            >
              {productOptions.length > 0 ? (
                <CheckboxList
                  options={productOptions}
                  selected={draft.products}
                  onChange={(values) => setDraft(prev => ({ ...prev, products: values }))}
                  searchable
                  searchPlaceholder={t('orders.searchProducts')}
                  maxHeight={180}
                  isRTL={isRTL}
                />
              ) : (
                <div className="py-4 text-center text-sm text-muted-foreground">
                  {t('common.noOptions')}
                </div>
              )}
            </CollapsibleSection>

            {/* License Plate Filter */}
            <CollapsibleSection
              title={t('orders.filterByLP')}
              activeCount={draft.vehicleLicensePlate ? 1 : 0}
              isRTL={isRTL}
              icon={<Truck className="h-4 w-4" />}
            >
              <div className="space-y-2">
                <Label htmlFor="lp-filter" className="text-xs text-muted-foreground">
                  {t('orders.lpPlaceholder')}
                </Label>
                <div className="relative">
                  <Truck className={cn(
                    'absolute top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground',
                    isRTL ? 'right-3' : 'left-3'
                  )} />
                  <Input
                    id="lp-filter"
                    type="text"
                    placeholder={t('orders.licensePlateExamplePlaceholder')}
                    value={draft.vehicleLicensePlate || ''}
                    onChange={(e) => setDraft(prev => ({ ...prev, vehicleLicensePlate: e.target.value }))}
                    className={cn(
                      'h-10 bg-muted/30 border-muted-foreground/20 focus:bg-background',
                      isRTL ? 'pr-10' : 'pl-10'
                    )}
                  />
                  {draft.vehicleLicensePlate && (
                    <button
                      type="button"
                      onClick={() => setDraft(prev => ({ ...prev, vehicleLicensePlate: '' }))}
                      className={cn(
                        'absolute top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-muted/80',
                        isRTL ? 'left-2' : 'right-2'
                      )}
                    >
                      <X className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  )}
                </div>
              </div>
            </CollapsibleSection>

            {/* Date Range Filter */}
            <CollapsibleSection
              title={t('orders.filterByOrderDate')}
              activeCount={draft.dateFrom || draft.dateTo ? 1 : 0}
              isRTL={isRTL}
              icon={<Calendar className="h-4 w-4" />}
            >
              <DateRangePicker
                dateRange={draft.dateFrom || draft.dateTo ? {
                  from: draft.dateFrom ? new Date(`${draft.dateFrom}T00:00:00`) : undefined,
                  to: draft.dateTo ? new Date(`${draft.dateTo}T00:00:00`) : undefined,
                } : undefined}
                onDateRangeChange={(range) => {
                  const toLocalDate = (date: Date) => {
                    const y = date.getFullYear()
                    const m = String(date.getMonth() + 1).padStart(2, '0')
                    const d = String(date.getDate()).padStart(2, '0')
                    return `${y}-${m}-${d}`
                  }

                  if (range?.from || range?.to) {
                    setDraft(prev => ({
                      ...prev,
                      dateFrom: range.from ? toLocalDate(range.from) : undefined,
                      dateTo: range.to ? toLocalDate(range.to) : undefined,
                    }))
                  } else {
                    setDraft(prev => ({ ...prev, dateFrom: undefined, dateTo: undefined }))
                  }
                }}
                isRTL={isRTL}
                placeholder={t('orders.selectDateRange')}
                presets={[
                  {
                    label: t('dateRangePicker.today'),
                    value: () => {
                      const today = new Date()
                      return { from: today, to: today }
                    },
                  },
                  {
                    label: t('dateRangePicker.thisWeek'),
                    value: () => {
                      const now = new Date()
                      const start = new Date(now)
                      start.setDate(now.getDate() - now.getDay())
                      const end = new Date(start)
                      end.setDate(start.getDate() + 6)
                      return { from: start, to: end }
                    },
                  },
                  {
                    label: t('dateRangePicker.thisMonth'),
                    value: () => {
                      const now = new Date()
                      const start = new Date(now.getFullYear(), now.getMonth(), 1)
                      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
                      return { from: start, to: end }
                    },
                  },
                  {
                    label: t('dateRangePicker.last30Days'),
                    value: () => {
                      const end = new Date()
                      const start = new Date()
                      start.setDate(end.getDate() - 30)
                      return { from: start, to: end }
                    },
                  },
                ]}
                className="w-full"
              />
            </CollapsibleSection>

            <CollapsibleSection
              title={t('orders.filterByDeliveryDate')}
              activeCount={draft.deliveryDateFrom || draft.deliveryDateTo ? 1 : 0}
              isRTL={isRTL}
              icon={<Calendar className="h-4 w-4" />}
            >
              <DateRangePicker
                dateRange={draft.deliveryDateFrom || draft.deliveryDateTo ? {
                  from: draft.deliveryDateFrom ? new Date(`${draft.deliveryDateFrom}T00:00:00`) : undefined,
                  to: draft.deliveryDateTo ? new Date(`${draft.deliveryDateTo}T00:00:00`) : undefined,
                } : undefined}
                onDateRangeChange={(range) => {
                  const toLocalDate = (date: Date) => {
                    const y = date.getFullYear()
                    const m = String(date.getMonth() + 1).padStart(2, '0')
                    const d = String(date.getDate()).padStart(2, '0')
                    return `${y}-${m}-${d}`
                  }

                  if (range?.from || range?.to) {
                    setDraft(prev => ({
                      ...prev,
                      deliveryDateFrom: range.from ? toLocalDate(range.from) : undefined,
                      deliveryDateTo: range.to ? toLocalDate(range.to) : undefined,
                    }))
                  } else {
                    setDraft(prev => ({ ...prev, deliveryDateFrom: undefined, deliveryDateTo: undefined }))
                  }
                }}
                isRTL={isRTL}
                placeholder={t('orders.selectDateRange')}
                presets={[
                  {
                    label: t('dateRangePicker.today'),
                    value: () => {
                      const today = new Date()
                      return { from: today, to: today }
                    },
                  },
                  {
                    label: t('dateRangePicker.thisWeek'),
                    value: () => {
                      const now = new Date()
                      const start = new Date(now)
                      start.setDate(now.getDate() - now.getDay())
                      const end = new Date(start)
                      end.setDate(start.getDate() + 6)
                      return { from: start, to: end }
                    },
                  },
                  {
                    label: t('dateRangePicker.thisMonth'),
                    value: () => {
                      const now = new Date()
                      const start = new Date(now.getFullYear(), now.getMonth(), 1)
                      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
                      return { from: start, to: end }
                    },
                  },
                  {
                    label: t('dateRangePicker.last30Days'),
                    value: () => {
                      const end = new Date()
                      const start = new Date()
                      start.setDate(end.getDate() - 30)
                      return { from: start, to: end }
                    },
                  },
                ]}
                className="w-full"
              />
            </CollapsibleSection>
          </div>
        </BaseDrawer>

        {/* Active filter count badge (visible when filters are applied) */}
        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <X className="h-4 w-4" />
            <span className="hidden sm:inline">{t('common.clearFilters')}</span>
            <span className="sm:hidden">({activeFilterCount})</span>
          </Button>
        )}
      </div>

      {/* Right side: columns */}
      <div className={cn(
        'flex items-center gap-2',
      )}>
        {/* Column visibility */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2">
              <Columns className="h-4 w-4" />
              <span className="hidden sm:inline">{t('orders.configureColumns')}</span>
              <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                {visibleColumnCount}
              </Badge>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align={isRTL ? 'start' : 'end'} className="w-64">
            <div className="px-2 py-1.5 text-xs text-muted-foreground">
              {t('orders.selectVisibleColumns')}
            </div>
            <DropdownMenuSeparator />
            {columnOptions.map((column) => (
              <DropdownMenuCheckboxItem
                key={column.key}
                checked={isColumnVisible(visibleColumns, column.key)}
                disabled={column.locked}
                onSelect={(event) => event.preventDefault()}
                onCheckedChange={(checked) => handleColumnVisibilityChange(column.key, checked === true)}
              >
                {t(column.labelKey)}
              </DropdownMenuCheckboxItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleResetColumns}>
              {t('common.reset')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}
